import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Card,
  CheckIcon,
  CopyIcon,
  EmptyState,
  GiftIcon,
  ShareIcon,
  Skeleton,
  TabBar,
} from '../components/ui';
import {
  DateRangeRow,
  periodToRange,
  type PeriodPreset,
} from '../components/activity/DateRangeRow';
import { EarningRow } from '../components/referrals/EarningRow';
import { MemberCard } from '../components/referrals/MemberCard';
import {
  useReferralEarnings,
  useReferralMembers,
  useReferralSummary,
} from '../hooks/useApiQuery';
import { copyText } from '../lib/clipboard';
import { tg } from '../lib/tg';
import { formatUsd } from '../lib/format';

type LevelFilter = 'all' | 1 | 2;
interface DateState {
  period: PeriodPreset;
  range: { from?: string; to?: string };
}

const INITIAL_DATE: DateState = { period: 'all', range: {} };

export function Referrals() {
  const { t, i18n } = useTranslation();
  const [level, setLevel] = useState<LevelFilter>('all');
  const [date, setDate] = useState<DateState>(INITIAL_DATE);
  const [friendFilter, setFriendFilter] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const summary = useReferralSummary();
  const members = useReferralMembers();
  const range = periodToRange(date.period, date.range);
  const earnings = useReferralEarnings({
    level: level === 'all' ? undefined : level,
    source_user_id: friendFilter ?? undefined,
    from_ts: range.from_ts,
    to_ts: range.to_ts,
  });

  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  // ⚠ Every hook MUST run on every render (Rules of Hooks). The
  // `if (!summary.data) return <Skeleton />` short-circuit below
  // bails the function out before the JSX render — so anything that
  // calls a hook (useMemo, useCallback, …) has to live above it.
  const memberItems = members.data?.items ?? [];
  const friendName = useMemo(() => {
    if (!friendFilter) return null;
    const m = memberItems.find((x) => x.user_id === friendFilter);
    if (!m) return null;
    return m.tg_username ? `@${m.tg_username}` : m.tg_first_name ?? '?';
  }, [friendFilter, memberItems]);

  const onCopy = async () => {
    if (!summary.data) return;
    const ok = await copyText(summary.data.ref_link);
    if (ok) {
      tg.hapticSuccess();
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      tg.hapticError();
    }
  };

  const onShare = async () => {
    if (!summary.data) return;
    const link = summary.data.ref_link;
    const text = t('referrals.share_text');
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Kit Pod', text, url: link });
      } catch {
        /* user cancelled */
      }
    } else {
      await onCopy();
    }
  };

  if (!summary.data) {
    return (
      <div className="mx-auto max-w-app px-5 pt-6">
        <Skeleton shape="block" height="220px" />
      </div>
    );
  }

  const s = summary.data;
  const totalEarned = Number(s.total_earned_usd);
  const pending = Number(s.pending_earned_usd);
  const totalPodSize = s.l1_referrals_count + s.l2_referrals_count;

  const platformFeePct = Number(s.platform_default_fee_pct ?? 20);
  const l1Pct = Number(s.l1_pct);
  const exampleFee = platformFeePct;
  const exampleCut = (platformFeePct * l1Pct) / 100;
  const exampleKeep = 100 - platformFeePct;

  return (
    <div className="mx-auto max-w-app space-y-5 px-5 pt-6">
      <div>
        <h1 className="pb-display text-[28px] leading-tight">
          {t('referrals.title')}
        </h1>
        <p className="mt-1.5 text-[14px] leading-relaxed text-tg-hint">
          {t('referrals.subtitle', { l1: s.l1_pct, l2: s.l2_pct })}
        </p>
      </div>

      <ShareHero
        refCode={s.ref_code}
        onCopy={onCopy}
        onShare={onShare}
        copied={copied}
      />

      {/* Worked example — how your share is calculated */}
      <Card pad="lg">
        <span className="pb-eyebrow">{t('referrals.example_title')}</span>
        <ul className="mt-3 space-y-1.5 text-[14px] leading-snug text-tg-text">
          <ExampleLine label={t('referrals.example_friend_earned')} value="+$100.00" />
          <ExampleLine
            label={t('referrals.example_service_fee')}
            value={`−${formatUsd(exampleFee)}`}
            tone="muted"
          />
          <ExampleLine
            label={t('referrals.example_your_cut')}
            value={`+${formatUsd(exampleCut)}`}
            tone="positive"
          />
          <li className="border-t border-pb-border-soft pt-1.5" />
          <ExampleLine
            label={t('referrals.example_friend_keeps')}
            value={formatUsd(exampleKeep)}
          />
        </ul>
        <p className="mt-3 text-[12px] leading-relaxed text-tg-hint">
          {t('referrals.disclaimer')}
        </p>
      </Card>

      {/* Earnings summary */}
      <Card pad="lg">
        <span className="pb-eyebrow">{t('referrals.earned')}</span>
        <div className="pb-numeral-lg mt-2 text-[36px] leading-none text-pb-success">
          <span className="mr-1 align-top text-[18px] text-tg-hint">$</span>
          {totalEarned.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
        {pending > 0 ? (
          <div className="mt-2">
            <Badge tone="warn">
              {t('referrals.pending', { amount: formatUsd(pending) })}
            </Badge>
          </div>
        ) : null}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <LevelStat
            label={t('referrals.level_one', { pct: s.l1_pct })}
            value={s.l1_referrals_count}
          />
          <LevelStat
            label={t('referrals.level_two', { pct: s.l2_pct })}
            value={s.l2_referrals_count}
          />
        </div>
      </Card>

      {/* Members — clicking a member filters the earnings list below */}
      <section>
        <h2 className="pb-eyebrow mb-2 px-1">
          {t('referrals.members_heading')}
        </h2>
        {memberItems.length > 0 ? (
          <Card pad="none" variant="flat">
            <ul className="divide-y divide-pb-border-soft">
              {memberItems.map((m) => (
                <li key={m.user_id}>
                  <MemberCard
                    member={m}
                    locale={locale}
                    active={friendFilter === m.user_id}
                    onClick={() =>
                      setFriendFilter((cur) =>
                        cur === m.user_id ? null : m.user_id,
                      )
                    }
                  />
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <Card pad="md" className="text-center">
            <p className="pb-caption">{t('referrals.empty_members_body')}</p>
          </Card>
        )}
      </section>

      {/* Earnings — filtered by level / date range / friend */}
      <section>
        <div className="mb-3 flex items-center justify-between gap-2 px-1">
          <h2 className="pb-eyebrow">{t('referrals.earnings_heading')}</h2>
          <TabBar
            size="sm"
            value={level}
            onChange={setLevel}
            options={[
              { value: 'all' as const, label: t('referrals.filter_all') },
              { value: 1 as const, label: 'L1' },
              { value: 2 as const, label: 'L2' },
            ]}
          />
        </div>
        <div className="mb-3">
          <DateRangeRow
            label={t('activity.filter_period')}
            period={date.period}
            range={date.range}
            onChange={(period, rng) => setDate({ period, range: rng })}
          />
        </div>
        {friendFilter ? (
          <div className="mb-3 flex items-center justify-between rounded-pb-md border border-pb-accent-line bg-pb-accent-soft/30 px-3 py-2">
            <span className="text-[12px] text-tg-text">
              {t('referrals.filtered_by_friend', { name: friendName })}
            </span>
            <button
              type="button"
              onClick={() => setFriendFilter(null)}
              className="pb-focus-ring rounded-pb-xs px-1.5 py-0.5 text-[12px] font-medium text-pb-accent hover:underline"
            >
              {t('referrals.clear_friend')}
            </button>
          </div>
        ) : null}
        {earnings.data?.items.length ? (
          <Card pad="none" variant="flat">
            <ul className="divide-y divide-pb-border-soft">
              {earnings.data.items.map((e) => (
                <li key={e.id}>
                  <EarningRow earning={e} locale={locale} />
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <EmptyState
            icon={<GiftIcon size={22} />}
            title={t('referrals.empty_earnings_title')}
            body={
              totalPodSize > 0
                ? t('referrals.empty_earnings_body_waiting', {
                    count: totalPodSize,
                  })
                : t('referrals.empty_earnings_body')
            }
          />
        )}
      </section>
    </div>
  );
}

function ShareHero({
  refCode,
  onCopy,
  onShare,
  copied,
}: {
  refCode: string;
  onCopy: () => void;
  onShare: () => void;
  copied: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="relative overflow-hidden rounded-pb-xl border border-pb-accent-line bg-gradient-to-br from-pb-accent-soft to-transparent p-5 pb-grain">
      <div className="pointer-events-none absolute right-[-36px] top-[-36px] h-32 w-32 rounded-full bg-pb-accent-soft blur-2xl" />
      <div className="pointer-events-none absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-pb-accent-soft blur-3xl" />
      <div className="relative">
        <div className="pb-eyebrow text-pb-accent">
          {t('referrals.code_label')}
        </div>
        <div
          className="pb-numeral-lg mt-1 text-[38px] leading-none text-tg-text"
          style={{ letterSpacing: '-0.02em' }}
        >
          {refCode}
        </div>
        <p className="mt-3 text-[13px] leading-relaxed text-tg-hint">
          {t('referrals.code_body')}
        </p>
        <div className="mt-4 flex gap-2">
          <Button
            variant="primary"
            fullWidth
            leading={<ShareIcon size={16} />}
            onClick={onShare}
          >
            {t('referrals.share_cta')}
          </Button>
          <Button
            variant="secondary"
            onClick={onCopy}
            leading={
              copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />
            }
            aria-label={t('common.copy')}
          />
        </div>
      </div>
    </div>
  );
}

function LevelStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-pb-md border border-pb-border-soft bg-pb-paper2 px-3 py-2.5">
      <div className="pb-eyebrow">{label}</div>
      <div className="pb-numeral mt-1 text-[22px] leading-none text-tg-text">
        {value}
      </div>
    </div>
  );
}

function ExampleLine({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'positive' | 'muted';
}) {
  return (
    <li className="flex items-baseline justify-between gap-3">
      <span className="text-tg-hint">{label}</span>
      <span
        className={
          tone === 'positive'
            ? 'pb-numeral text-pb-success'
            : tone === 'muted'
              ? 'pb-numeral text-tg-hint'
              : 'pb-numeral'
        }
      >
        {value}
      </span>
    </li>
  );
}
